<script setup>
import { computed, ref, watch } from 'vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';

const { hierarchicalSchemas, modelValue } = defineProps({
  hierarchicalSchemas: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);
const { $t } = useCommonImports();

// UI state
const search = ref('');
const expanded = ref({});

function toggle(node) {
  if (!node || !node.uid)
    return;
  expanded.value[node.uid] = !expanded.value[node.uid];
}
function isExpanded(node) {
  return !!expanded.value[node.uid];
}

function onLeafClick(node) {
  const schema = node._schema || node;
  if (schema)
    emit('update:modelValue', schema);
}

// simple recursive filter that preserves folder structure
function filterNodes(nodes, q) {
  if (!q)
    return nodes || [];
  const term = q.toLowerCase();
  function filterNode(node) {
    if (node.children && node.children.length) {
      const children = node.children.map(filterNode).filter(Boolean);
      if (children.length)
        return { ...node, children };
      if ((node.label || '').toLowerCase().includes(term))
        return { ...node };
      return null;
    }
    if ((node.label || '').toLowerCase().includes(term))
      return node;
    if (node._schema && ((node._schema.label || node._schema.name || '').toLowerCase().includes(term)))
      return node;
    return null;
  }
  return (nodes || []).map(filterNode).filter(Boolean);
}

const displayNodes = computed(() => filterNodes(hierarchicalSchemas, search.value));

// while searching, auto-expand parent nodes of matched results and restore state when cleared
const _searchSavedExpanded = ref(null);
watch(search, (val) => {
  const hasTerm = !!(val && val.toString().trim());
  if (hasTerm) {
    // save current expansion state once per search session
    if (_searchSavedExpanded.value === null)
      _searchSavedExpanded.value = { ...expanded.value };

    // expand every node present in the filtered tree (these are parents of matches)
    const stack = [...(displayNodes.value || [])];
    while (stack.length) {
      const node = stack.pop();
      if (!node)
        continue;
      if (node.children && node.children.length) {
        expanded.value[node.uid] = true;
        stack.push(...node.children);
      }
    }
  }
  else {
    // restore previous expansion state when search cleared
    if (_searchSavedExpanded.value !== null) {
      expanded.value = { ..._searchSavedExpanded.value };
      _searchSavedExpanded.value = null;
    }
  }
});

const search_input = ref(null);

function onMenuOpen() {
  setTimeout(() => {
    if (search_input.value) {
      search_input.value.focus();
    }
  }, 100);
}
</script>

<template>
  <hawk-menu position="fixed" additional_trigger_classes="!ring-0 !focus:ring-0 !px-0 !py-0 w-full text-left" @open="onMenuOpen">
    <template #trigger="{ open }">
      <slot name="trigger" :open="open" :model-value="modelValue">
        <div>
          <div class="border rounded-lg p-2 flex items-center justify-between cursor-pointer" :class="{ 'bg-primary-25 border-primary-200 text-primary-500': !modelValue }">
            <div v-if="modelValue" class="text-sm font-normal  text-gray-900">
              {{ modelValue.label }}
            </div>
            <div v-else>
              <div class="text-sm text-gray-700">
                {{ $t('Choose a dataset') }}
              </div>
            </div>
            <div>
              <IconHawkChevronDown class="size-4 transition-transform" :class="{ 'rotate-180': open }" />
            </div>
          </div>
        </div>
      </slot>
    </template>

    <template #content="{ close }">
      <div
        class="rounded-lg w-72 flex flex-col bg-white" @keydown.enter.stop
        @keydown.space.stop
      >
        <div class="p-2">
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <IconHawkSearch class="text-gray-600 size-4" />
            </div>
            <input
              ref="search_input"
              v-model="search"
              name="input"
              autocomplete="off"
              :placeholder="$t('Search')"
              autofocus
              class="block w-full py-1.5 text-gray-900 pl-10 placeholder:text-gray-500 focus:!ring-0 focus:outline-none text-sm sm:leading-6 rounded-lg"
            >
          </div>
        </div>

        <div class="min-h-[200px] max-h-80 overflow-auto scrollbar font-normal border-t px-2 pb-2">
          <ul class="space-y-1">
            <bi-query-builder-table-hierarchy
              v-for="node in displayNodes"
              :key="node.uid"
              :node="node"
              :is-expanded-fn="isExpanded"
              @toggle="toggle"
              @select="(n) => { onLeafClick(n); close(); }"
            />
          </ul>
        </div>
      </div>
    </template>
    <template #footer="{ close }">
      <slot name="footer" :close="close" />
    </template>
  </hawk-menu>
</template>
